import React from 'react';
import { Box, Typography } from '@mui/material';
import { InvoiceTax } from '../add-invoice';

import buildApiRequestConfig from '../../../api/url-builder';
import routes from '../../../api/routes';
import useApi from '../../../hooks/use-api';
import { useSettings } from '../../../providers/settings-provider';

interface InvoiceTaxesProps {
    subTotal: number;
    taxes: InvoiceTax[];
    grandTotal: number;
    onChange: (taxes: InvoiceTax[]) => void;
    entityId?: number;
    items?: Array<{ quantity: number; unitPrice: number; appliedTaxes?: string[] }>;
}

const InvoiceTaxes: React.FC<InvoiceTaxesProps> = ({ subTotal, taxes, grandTotal, entityId, items = [] }) => {
    const { formatAmount } = useSettings();
    const [taxData, setTaxData] = React.useState<{
        id: number;
        name: string;
        rate: number;
        type: 'added' | 'included';
    }[]>([]);
    const [fetchTaxes, setFetchTaxes] = useApi();

    // Calculate tax amount for a specific tax based on item-level applications
    const calculateTaxAmount = (taxId: number, taxRate: number) => {
        return items.reduce((total, item) => {
            if (item.appliedTaxes?.includes(String(taxId))) {
                const itemSubtotal = item.quantity * item.unitPrice;
                return total + (itemSubtotal * taxRate / 100);
            }
            return total;
        }, 0);
    };

    React.useEffect(() => {
        if (!entityId) return;
        const { url, method } = buildApiRequestConfig(routes.taxDefinitions.fetchByEntity, [{ param: 'entityId', value: entityId }]);
        setFetchTaxes({ url, method });
    }, [entityId, setFetchTaxes]);

    React.useEffect(() => {
        const { data, isLoading } = fetchTaxes;
        if (data && !isLoading) {
            const newTaxData = data.map((tax: any) => ({
                id: tax.id,
                name: tax.name,
                rate: tax.rate,
                type: tax.type
            }));
            setTaxData(newTaxData);
        }
    }, [fetchTaxes]);

    return (
        <Box sx={{ width: '100%', flex: 1 }}>
            <Typography sx={{ fontWeight: 600, color: 'var(--color-default-text)' }}>
                Summary
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography fontSize={14} fontWeight={500}>Sub Total</Typography>
                <Typography fontSize={14} fontWeight={600}>
                    {formatAmount?.(subTotal)}
                </Typography>
            </Box>
            {taxData.map((tax) => {
                const taxAmount = calculateTaxAmount(tax.id, tax.rate);
                // Only show tax if it's applied to at least one item
                if (taxAmount === 0) return null;
                
                return (
                    <Box key={tax.id} display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                        <Typography fontSize={14}>
                            {tax.name} ({tax.rate}%)
                        </Typography>
                        <Typography fontSize={14} fontWeight={600}>
                            {formatAmount?.(taxAmount)}
                        </Typography>
                    </Box>
                );
            })}
            <Box 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center" 
                sx={{ 
                    borderTop: '1px solid var(--color-primary)', 
                    pt: 1,
                    mt: 1
                }}
            >
                <Typography fontSize={16} fontWeight={700} color="var(--color-primary)">
                    Grand Total
                </Typography>
                <Typography fontSize={16} fontWeight={700} color="var(--color-primary)">
                    {formatAmount?.(grandTotal)}
                </Typography>
            </Box>
        </Box>
    );
};

export default InvoiceTaxes;